import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Demand Notification opt-in panel for pros.
 * - Requests browser Notification permission
 * - Subscribes the service worker to Web Push (VAPID)
 * - POSTs subscription to /api/push/subscribe
 * - Fetches + updates preferences via /api/push/preferences
 * - "Send test" button confirms end-to-end delivery
 */

// Convert a base64-url VAPID public key to Uint8Array (required by pushManager).
function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
    return arr;
}

export default function PushNotificationToggle({ serviceCategories = [] }) {
    const [supported, setSupported] = useState(true);
    const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
    const [prefs, setPrefs] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const ok = "serviceWorker" in navigator && "PushManager" in window;
        setSupported(ok);
    }, []);

    useEffect(() => {
        api.get("/push/preferences")
            .then(({ data }) => setPrefs(data))
            .catch(() => {});
    }, []);

    const enable = async () => {
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== "granted") {
                toast.error("Notification permission denied. Enable in browser settings.");
                setBusy(false);
                return;
            }
            const reg = (await navigator.serviceWorker.getRegistration())
                || (await navigator.serviceWorker.register("/service-worker.js"));
            await navigator.serviceWorker.ready;

            const { data } = await api.get("/push/public-key");
            const key = urlBase64ToUint8Array(data.public_key);
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: key,
            });
            const subJson = sub.toJSON();
            await api.post("/push/subscribe", {
                endpoint: subJson.endpoint,
                keys: subJson.keys,
                user_agent: navigator.userAgent,
                categories: serviceCategories,
            });
            const { data: fresh } = await api.get("/push/preferences");
            setPrefs(fresh);
            toast.success("Push notifications enabled. You'll be the first to know when a demand lands.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not enable push");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) {
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
                    await sub.unsubscribe();
                }
            }
            await api.patch("/push/preferences", { enabled: false });
            const { data: fresh } = await api.get("/push/preferences");
            setPrefs(fresh);
            toast.success("Push notifications disabled.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const sendTest = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/push/test");
            if (data.delivered === 0) {
                toast.warning(`Attempted ${data.attempted} — none delivered. Check browser notification permission.`);
            } else {
                toast.success(`Test push sent to ${data.delivered} device${data.delivered > 1 ? "s" : ""}.`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!supported) {
        return (
            <div className="p-5 border border-white/10 rounded-sm bg-card" data-testid="push-panel-unsupported">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert size={14} className="text-amber-300" strokeWidth={1.5} />
                    <span className="overline">Demand Notifications</span>
                </div>
                <p className="text-sm text-muted-foreground">
                    Your browser doesn&apos;t support push notifications. Try Chrome, Edge, or Firefox on desktop / Android.
                </p>
            </div>
        );
    }

    const enabled = Boolean(prefs?.enabled) && permission === "granted";

    return (
        <section
            className="p-6 border border-white/10 rounded-sm bg-card"
            data-testid="push-notification-panel"
        >
            <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        {enabled ? (
                            <Bell size={14} className="gold-text" strokeWidth={1.5} />
                        ) : (
                            <BellOff size={14} className="text-muted-foreground" strokeWidth={1.5} />
                        )}
                        <span className="overline">Demand Notifications</span>
                        {enabled && (
                            <span className="text-[9px] tracking-[0.25em] uppercase text-emerald-400 flex items-center gap-1">
                                <CheckCircle2 size={10} strokeWidth={2} /> Active
                            </span>
                        )}
                    </div>
                    <h3 className="font-serif text-xl">Get the ping. Beat the competition.</h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md leading-relaxed">
                        When a customer creates a booking in your service category, you get a browser
                        push the instant it lands. Even if this tab isn&apos;t open.
                    </p>
                </div>
                <Switch
                    checked={enabled}
                    disabled={busy}
                    onCheckedChange={(v) => (v ? enable() : disable())}
                    data-testid="push-toggle-switch"
                    className="mt-1"
                />
            </div>

            {enabled && prefs && (
                <div className="grid sm:grid-cols-3 gap-4 mb-4 text-xs">
                    <Stat label="Devices" value={prefs.device_count || 0} testid="push-devices-count" />
                    <Stat label="Watching" value={(prefs.categories || []).length || "All"} testid="push-cats-count" />
                    <div className="p-3 border border-white/10 rounded-sm bg-black/30">
                        <div className="overline mb-1">Quiet hours</div>
                        <div className="text-foreground">{prefs.quiet_hours || "Off"}</div>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-3">
                {enabled && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={sendTest}
                        disabled={busy}
                        className="border-white/15 text-foreground rounded-sm uppercase tracking-[0.2em] text-[10px]"
                        data-testid="push-test-btn"
                    >
                        Send test push
                    </Button>
                )}
                {permission === "denied" && (
                    <div className="text-[11px] text-amber-300 flex items-center gap-2" data-testid="push-permission-denied">
                        <ShieldAlert size={12} strokeWidth={1.5} />
                        Browser blocked notifications — enable them in your site settings.
                    </div>
                )}
            </div>
        </section>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="p-3 border border-white/10 rounded-sm bg-black/30" data-testid={testid}>
            <div className="overline mb-1">{label}</div>
            <div className="font-serif text-lg text-foreground tabular-nums">{value}</div>
        </div>
    );
}
